import React, { useCallback, useMemo } from 'react';

import { Center, Link } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { NavigationLink } from '../models/Header';
import { TextType } from '../models/Text';

import Text from './Text';

interface NavLinkProps {
  link: NavigationLink;
  color?: string;
  onClick?: () => void;
}

const NavLink = ({
  link,
  color = 'white',
  onClick = () => null,
}: NavLinkProps) => {
  const { t } = useTranslation();
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();

  const selectedLink = useMemo(() => {
    const [targetPath, targetHash] =
      link.linkTo.split('#');

    const normalizedPath = targetPath || '/';

    if (targetHash) {
      return (
        pathname === normalizedPath &&
        hash === `#${targetHash}`
      );
    }

    return pathname === normalizedPath;
  }, [pathname, hash, link.linkTo]);

  const smoothScrollToElement = useCallback(
    (element: HTMLElement) => {
      const headerOffset = 80;

      const targetY =
        element.getBoundingClientRect().top +
        window.scrollY -
        headerOffset;

      const startY = window.scrollY;
      const distance = targetY - startY;

      // Increase this number to make the scroll slower.
      const duration = 1200;

      let startTime: number | null = null;

      const easeInOutCubic = (progress: number) =>
        progress < 0.5
          ? 4 * progress * progress * progress
          : 1 -
            Math.pow(
              -2 * progress + 2,
              3
            ) /
              2;

      const animateScroll = (
        currentTime: number
      ) => {
        if (startTime === null) {
          startTime = currentTime;
        }

        const elapsed =
          currentTime - startTime;

        const progress = Math.min(
          elapsed / duration,
          1
        );

        const easedProgress =
          easeInOutCubic(progress);

        window.scrollTo({
          top:
            startY +
            distance * easedProgress,
          left: 0,
          behavior: 'auto',
        });

        if (progress < 1) {
          window.requestAnimationFrame(
            animateScroll
          );
        }
      };

      window.requestAnimationFrame(
        animateScroll
      );
    },
    []
  );

  const handleClick = useCallback(
    (
      event: React.MouseEvent<HTMLAnchorElement>
    ) => {
      event.preventDefault();

      const [targetPath, targetHash] =
        link.linkTo.split('#');

      const normalizedPath =
        targetPath || '/';

      // Same page + anchor:
      // do not navigate/remount, just scroll.
      if (
        targetHash &&
        pathname === normalizedPath
      ) {
        const element =
          document.getElementById(
            targetHash
          );

        if (element) {
          smoothScrollToElement(element);
        }

        // Update React Router's hash so the
        // selected navbar link becomes bold.
        navigate(
          {
            pathname: normalizedPath,
            hash: `#${targetHash}`,
          },
          {
            replace: true,
          }
        );

        onClick?.();

        return;
      }

      // Different page:
      // navigate normally. ScrollToTop.tsx
      // will handle the target anchor once
      // that page has rendered.
      navigate(link.linkTo);

      onClick?.();
    },
    [
      link.linkTo,
      pathname,
      navigate,
      onClick,
      smoothScrollToElement,
    ]
  );

  return (
    <Center
      paddingX="12px"
      paddingY="10px"
      marginX="12px"
    >
      <Text
        type={
          selectedLink
            ? TextType.Inter12BoldNav
            : TextType.Inter12LightNav
        }
        others={{
          textTransform: 'lowercase',
        }}
        color={color}
      >
        <Link
          href={link.linkTo}
          color="inherit"
          textDecoration="none"
          textUnderlineOffset={0}
          _hover={{
            textDecoration: 'none',
          }}
          onClick={handleClick}
        >
          {t(`header.${link.title}`)}
        </Link>
      </Text>
    </Center>
  );
};

export default NavLink;